<script setup lang="ts">
import WrDesignHeroCard from "~/components/molecules/WrDesignHeroCard.vue";
import WrDesignShineText from "~/components/molecules/WrDesignShineText.vue";
import WrDesignProcessCardsGrid from "~/components/molecules/WrDesignProcessCardsGrid.vue";
import WrDesignLevelUpYourCreativityWithAi from "~/components/molecules/WrDesignLevelUpYourCreativityWithAi.vue";
import WrProblemSolutionSection from "~/components/molecules/WrProblemSolutionSection.vue";
import WrDesignServiceCardsSection from "~/components/molecules/WrDesignServiceCardsSection.vue";
import WrDesignTailoredSection from "~/components/molecules/WrDesignTailoredSection.vue";
import WrDesignIconLineDecorativeSquaresCards from "~/components/molecules/WrDesignIconLineDecorativeSquaresCards.vue";
import WrDesignWhyChooseUsCard from "~/components/molecules/WrDesignWhyChooseUsCard.vue";
import WrDesignRealWorldApplicationsSection from "~/components/molecules/WrDesignRealWorldApplicationsSection.vue";
import WrDesignReadyToElevateYourBrand from "~/components/molecules/WrDesignReadyToElevateYourBrand.vue";
import WrDesignFourCardTransformGridSection from "~/components/molecules/WrDesignFourCardTransformGridSection.vue";
import WrDesignIconNumberHeadingCards from "~/components/molecules/WrDesignIconNumberHeadingCards.vue";
import WrDesignFaqsSection from "~/components/molecules/WrDesignFaqsSection.vue";
import WrDesignStatsCard from "~/components/molecules/WrDesignStatsCard.vue";
import WrTestimonialCardCarousel from "~/components/molecules/WrTestimonialCardCarousel.vue";
import WrIcon from "~/components/global/WrIcon.vue";

const WrContactFormPopup = defineAsyncComponent(
  () => import("~/components/molecules/WrContactFormPopup.vue")
);

/* ─── State & Composables ─── */
const visible = ref<boolean>(false);
const openModal = (): void => {
  visible.value = true;
};

/* ─── Fetch Content ─── */
const { data } = await useFetch(
  `${useRequestURL().origin}/content/solutions/restaurant-management-software-development-services-content.json`,
);
if (!data.value) throw createError({ statusCode: 404, statusMessage: "Page content not found" });
const content = data.value as any;

const {
  meta_tags,
  hero,
  shineText,
  quickHits,
  businessOutcomes,
  whyItMatters,
  capabilities,
  processMotion,
  techStack,
  stackIntegration,
  integrationCards: integrationCardsData,
  useCases,
  whyChooseUs,
  cta,
  faq,
} = content;

/* ─── SEO Metadata & Schema ─── */
useHead({
  title: meta_tags.title,
  link: [{ rel: "canonical", href: meta_tags.canonical }],
  meta: [...meta_tags.meta, { name: "robots", content: "index, follow" }],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        name: meta_tags.serviceSchema.name,
        serviceType: meta_tags.serviceSchema.serviceType,
        url: meta_tags.canonical,
        provider: {
          "@type": "Organization",
          name: "WebReinvent",
          url: "https://webreinvent.com",
        },
        description: meta_tags.serviceSchema.description,
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://webreinvent.com" },
          { "@type": "ListItem", position: 2, name: "Solutions", item: "https://webreinvent.com/solutions" },
          { "@type": "ListItem", position: 3, name: meta_tags.serviceSchema.breadcrumbName, item: meta_tags.canonical },
        ],
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.faqItems.map((item: { title: string; description: string }) => ({
          "@type": "Question",
          name: item.title,
          acceptedAnswer: { "@type": "Answer", text: item.description },
        })),
      }),
    },
  ],
});

useSeoMeta({
  ogTitle: meta_tags.meta.find((m: any) => m.property === 'og:title')?.content || meta_tags.title,
  ogDescription: meta_tags.meta.find((m: any) => m.property === 'og:description')?.content,
  ogImage: meta_tags.meta.find((m: any) => m.property === 'og:image')?.content,
  ogUrl: meta_tags.canonical,
  ogType: 'website',
  twitterTitle: meta_tags.meta.find((m: any) => m.name === 'twitter:title')?.content,
  twitterDescription: meta_tags.meta.find((m: any) => m.name === 'twitter:description')?.content,
  twitterImage: meta_tags.meta.find((m: any) => m.name === 'twitter:image')?.content,
  twitterCard: 'summary_large_image',
  twitterSite: '@webreinvent',
  ogSiteName: 'WebReinvent',
});

definePageMeta({
  layout: "services-layout",
});

/* ─── Mapped Data ─── */

// ProcessCardsGrid: { title, description, svgIcon }
const quickHitCards = computed(() =>
  quickHits.items.map((item: any) => ({
    title: item.title,
    description: item.description,
    svgIcon: item.svgIcon,
  }))
);

// LevelUpYourCreativity: { svgIcon, title, description }
const businessOutcomeCards = computed(() =>
  businessOutcomes.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.description,
  }))
);

// ServiceCardsSection: { svgIcon, title, desc }
const capabilitiesCards = computed(() =>
  capabilities.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    desc: card.desc,
  }))
);

// TailoredSection: { svgIcon, title, description }
const motionCards = computed(() =>
  processMotion.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.desc,
  }))
);

// IconLineDecorativeSquares: { number, title, description, svgIcon }
const techStackCards = computed(() =>
  techStack.items.map((card: any, i: number) => ({
    number: `0${i + 1}`,
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.description,
  }))
);

// RealWorldApplications: { number, title, description }
const integrationCards = computed(() =>
  integrationCardsData.items.map((card: any, i: number) => ({
    number: `0${i + 1}`,
    title: card.title,
    description: card.description,
  }))
);

// ReadyToElevateYourBrand: { svgIcon, title, description }
const useCaseCards = computed(() =>
  useCases.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.desc,
  }))
);

// FourCardTransformGrid: { svgIcon, title, description }
const whyChooseUsCards = computed(() =>
  whyChooseUs.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.description,
  }))
);

// IconNumberHeadingCards: { number, title, desc, svgIcon }
const ctaCards = computed(() =>
  cta.cards.map((card: any, i: number) => ({
    number: `0${i + 1}`,
    svgIcon: card.svgIcon,
    title: card.title,
    desc: card.description,
  }))
);

const faqItems = computed(() => faq.faqItems);

// Clean hero alt text by stripping HTML tags (e.g. <br />)
const heroAlt = computed(() => String(hero.heading).replace(/<[^>]*>/g, '').trim());
</script>

<template>
  <main class="min-h-screen px-4 lg:px-0">
    <div class="max-w-screen-xl mx-auto">

      <!-- Section 1: Hero Card -->
      <header class="pt-32">
        <WrDesignHeroCard
          :heading="hero.heading"
          :subcopy="hero.subcopy"
          :subcopy-class="hero.subcopyClass"
          :cta-text="hero.ctaText"
          :image-src="hero.imageSrc"
          :alt="heroAlt"
          @cta-clicked="openModal"
        />
      </header>

      <!-- Section 1.5: Shine Text -->
      <section class="my-8" aria-label="Core Value Statement">
        <WrDesignShineText :text="shineText" />
      </section>

      <!-- Section 2: Quick Hits (ProcessCardsGrid) -->
      <section aria-label="Restaurant System Capabilities at a Glance">
        <WrDesignProcessCardsGrid
          :heading="quickHits.heading"
          :subcopy="quickHits.subcopy"
          :cards="quickHitCards"
          :cta-text="quickHits.ctaText"
          @cta-clicked="openModal"
        >
          <template v-for="(card, idx) in quickHitCards" :key="card.title" #[`icon-${idx}`]>
            <WrIcon :name="card.svgIcon" class="size-6 sm:size-8 text-[#501D71] dark:text-white" />
          </template>
        </WrDesignProcessCardsGrid>
      </section>

      <!-- Section 3: Business Outcomes (LevelUpYourCreativityWithAi) -->
      <section aria-label="Business Outcomes">
        <WrDesignLevelUpYourCreativityWithAi
          :heading="businessOutcomes.heading"
          :subcopy="businessOutcomes.subcopy"
          :cards="businessOutcomeCards"
        />
      </section>

      <!-- Section 4: Problem / Solution (ProblemSolutionSection) -->
      <section aria-labelledby="why-it-matters-heading">
        <WrProblemSolutionSection
          id="why-it-matters-heading"
          :heading="whyItMatters.heading"
          :intro="whyItMatters.subcopy"
          :problem-label="whyItMatters.lossLabel"
          :problems="whyItMatters.losses"
          :solution-label="whyItMatters.deliverLabel"
          :solutions="whyItMatters.delivers"
          :quote="whyItMatters.quote"
          :cta-text="whyItMatters.ctaText"
          :bg-image-light="whyItMatters.bgImageLight"
          :bg-image-dark="whyItMatters.bgImageDark"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 5: Capabilities (ServiceCardsSection) -->
      <section aria-label="Restaurant System Capabilities Overview">
        <WrDesignServiceCardsSection
          :heading-line1="capabilities.headingLine1"
          :heading-line2="capabilities.headingLine2"
          :subcopy="capabilities.subcopy"
          :cards="capabilitiesCards"
          :cta-text="capabilities.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 6: How the System Works (TailoredSection) -->
      <section aria-label="Restaurant System Workflow">
        <WrDesignTailoredSection
          :heading="processMotion.heading"
          :subcopy="processMotion.subcopy"
          :cards="motionCards"
          :cta-text="processMotion.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 7: Tech Stack (IconLineDecorativeSquaresCards) -->
      <section aria-label="Enterprise Architecture">
        <WrDesignIconLineDecorativeSquaresCards
          :heading="techStack.heading"
          :subcopy="techStack.subcopy"
          :cards="techStackCards"
          :cta-text="techStack.ctaText"
          @cta-clicked="openModal"
        >
          <template v-for="(card, idx) in techStackCards" :key="card.title" #[`icon-${idx}`]>
            <WrIcon :name="card.svgIcon" class="size-6 sm:size-8 text-[#501D71] dark:text-[#CDA0FE]" />
          </template>
        </WrDesignIconLineDecorativeSquaresCards>
      </section>

      <!-- Section 8: Integration Banner (WhyChooseUsCard) -->
      <!-- <section aria-label="Integration Stack">
        <WrDesignWhyChooseUsCard
          :heading="stackIntegration.heading"
          :subcopy="stackIntegration.subcopy"
          :cta-text="stackIntegration.ctaText"
          :image-dark-src="stackIntegration.imageDarkSrc"
          :image-light-src="stackIntegration.imageLightSrc"
          @cta-clicked="openModal"
        />
      </section> -->

      <!-- Section 9: Integration Cards (RealWorldApplicationsSection) -->
      <section aria-label="Integrations and Systems">
        <WrDesignRealWorldApplicationsSection
          :single-line-heading="integrationCards.heading"
          :subtitle="integrationCards.subcopy"
          :cards="integrationCards"
          :cta-text="integrationCards.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 10: Real-World Use Cases (ReadyToElevateYourBrand) -->
      <section aria-label="Real-World Use Cases">
        <WrDesignReadyToElevateYourBrand
          :heading="useCases.heading"
          :subcopy="useCases.subcopy"
          :cards="useCaseCards"
          :cta-text="useCases.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 11: Why Choose Us (FourCardTransformGridSection) -->
      <section aria-label="Why Choose WebReinvent">
        <WrDesignFourCardTransformGridSection
          :heading="whyChooseUs.heading"
          :subcopy="whyChooseUs.subcopy"
          :cards="whyChooseUsCards"
          :cta-text="whyChooseUs.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 12: CTA Block (IconNumberHeadingCards) -->
      <section aria-label="Call to Action">
        <WrDesignIconNumberHeadingCards
          :heading="cta.heading"
          :subcopy="cta.subcopy"
          :cards="ctaCards"
          :cta-text="cta.ctaText"
          @cta-clicked="openModal"
        >
          <template v-for="(card, idx) in ctaCards" :key="card.title" #[`icon-${idx}`]>
            <WrIcon :name="card.svgIcon" class="size-8 max-sm:size-6 text-[#501D71] dark:text-white" />
          </template>
        </WrDesignIconNumberHeadingCards>
      </section>

      <!-- Section 13: Testimonials -->
      <section class="relative mt-16 overflow-hidden max-w-screen-xl mx-auto px-4 z-20">
        <h2 class="text-center mb-11">
          <WrGradientText text="Trusted for excellence and efficiency" />
          <p class="text-lg text-[var(--foreground)] font-anvtgrade-book">
            WebReinvent is trusted by leading organisations to enhance their
            development and operational efficiency.
          </p>
        </h2>
        <WrTestimonialCardCarousel />
        <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7">
          View All
        </WrButton>
      </section>

      <!-- Section 14: FAQ (Filtered accordion with tags) -->
      <section aria-label="Frequently asked questions">
        <WrDesignFaqsSection
          :heading="faq.heading"
          :suffix="faq.suffix"
          :subtext="faq.subtext"
          :filter-tags="faq.filterTags"
          :faq-items="faqItems"
          :cta-text="faq.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 15: Stats Card -->
      <section class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" aria-label="Company Statistics">
        <WrDesignStatsCard />
      </section>

    </div>
  </main>

  <WrContactFormPopup :isOpen="visible" class="z-30" @close="visible = false" />
</template>

<style scoped>
:deep(.hero-image-wrapper) {
  max-width: 500px;
}
</style>
